import React from 'react';
import {
	Box,
	Button,
	FormControlLabel,
	MenuItem,
	Paper,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
	REMINDER_INTERVAL_OPTIONS,
	REVOCATION_MINUTE_OPTIONS,
} from '../constants';
import { SectionCard, SectionHeader } from '../components/SettingsShell';

const ProposalsSection = ({
	formData,
	setFormData,
	saving,
	onSave,
}) => (
	<SectionCard>
		<SectionHeader
			icon={DescriptionOutlinedIcon}
			title="Proposals"
			description="Defaults for new jobs and automatic customer reminders."
		/>

		<Box component="form" onSubmit={onSave} noValidate>
			<Stack spacing={{ xs: 2.5, sm: 3 }}>
				<Stack spacing={1.5}>
					<Typography variant="subtitle1" fontWeight={700}>
						New job default
					</Typography>
					<Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
						<FormControlLabel
							control={
								<Switch
									name="default_itemized_bids"
									checked={formData.default_itemized_bids}
									onChange={(event) =>
										setFormData((prev) => ({
											...prev,
											default_itemized_bids: event.target.checked,
										}))
									}
								/>
							}
							label={
								<Box>
									<Typography variant="body1" fontWeight={700}>
										Itemized proposals by default
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 0.5, lineHeight: 1.6 }}
									>
										When on, new jobs open with a line-by-line breakdown on the
										customer proposal PDF. When off, you enter one project total
										instead.
									</Typography>
								</Box>
							}
							sx={{ alignItems: 'flex-start', ml: 0, mr: 0, width: '100%' }}
						/>
					</Paper>
				</Stack>

				<Stack spacing={1.5}>
					<Typography variant="subtitle1" fontWeight={700}>
						Bid reminders
					</Typography>
					<Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
						<Stack spacing={1.5}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ lineHeight: 1.6 }}
							>
								Email customers a nudge to review a pending proposal on this
								cadence until it expires. Each reminder notes the expiration date.
								Your organization email is BCC&apos;d, and each send appears in
								the job activity feed.
							</Typography>
							<TextField
								select
								label="Reminder frequency"
								name="bid_reminder_interval_days"
								value={formData.bid_reminder_interval_days}
								onChange={(event) =>
									setFormData((prev) => ({
										...prev,
										bid_reminder_interval_days: Number(event.target.value),
									}))
								}
								sx={{ maxWidth: { sm: 280 } }}
							>
								{REMINDER_INTERVAL_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Stack>
					</Paper>
				</Stack>

				<Stack spacing={1.5}>
					<Typography variant="subtitle1" fontWeight={700}>
						Acceptance cancellation window
					</Typography>
					<Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
						<Stack spacing={1.5}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ lineHeight: 1.6 }}
							>
								After a customer accepts from email, they can cancel on the
								confirmation page for this many minutes. When the window ends,
								acceptance becomes binding. One-click accept from email is
								unchanged.
							</Typography>
							<TextField
								select
								label="Cancellation window"
								name="acceptance_revocation_minutes"
								value={formData.acceptance_revocation_minutes}
								onChange={(event) =>
									setFormData((prev) => ({
										...prev,
										acceptance_revocation_minutes: Number(event.target.value),
									}))
								}
								sx={{ maxWidth: { sm: 320 } }}
							>
								{REVOCATION_MINUTE_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Stack>
					</Paper>
				</Stack>

				<Box
					sx={{
						display: 'flex',
						justifyContent: { xs: 'stretch', sm: 'flex-end' },
					}}
				>
					<Button
						type="submit"
						variant="contained"
						disabled={saving}
						sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 160 } }}
					>
						{saving ? 'Saving...' : 'Save changes'}
					</Button>
				</Box>
			</Stack>
		</Box>
	</SectionCard>
);

export default ProposalsSection;
